import { useEffect, useMemo, useState } from "react";
import { Gauge, Fuel, Cog, Car as CarIcon, MessageCircle, BadgeCheck, Loader2, Eye, Search, SlidersHorizontal, X } from "lucide-react";
import { Reveal, SectionHeading } from "../Reveal";
import api, { WHATSAPP_NUMBER } from "../../lib/api";
import { CarDetailModal } from "./CarDetailModal";

const spec = (Icon, label) =>
  label ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <Icon size={14} className="text-navy" /> {label}
    </span>
  ) : null;

const toNum = (v) => (v ? Number(String(v).replace(/[^0-9.]/g, "")) || 0 : 0);
const selectCls = "rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-navy outline-none focus:border-navy";

export const AutoSales = ({ embedded = false }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [bodyType, setBodyType] = useState("all");
  const [fuelType, setFuelType] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api
      .get("/cars")
      .then(({ data }) => setCars(data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, []);

  const bodyTypes = useMemo(() => [...new Set(cars.map((c) => c.body_type).filter(Boolean))], [cars]);
  const fuelTypes = useMemo(() => [...new Set(cars.map((c) => c.fuel_type).filter(Boolean))], [cars]);

  const visibleCars = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = cars.filter((c) => {
      if (bodyType !== "all" && c.body_type !== bodyType) return false;
      if (fuelType !== "all" && c.fuel_type !== fuelType) return false;
      if (q) {
        const hay = [c.title, c.make, c.model, c.description, ...(c.features || [])].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const sorters = {
      "price-asc": (a, b) => toNum(a.price) - toNum(b.price),
      "price-desc": (a, b) => toNum(b.price) - toNum(a.price),
      "year-desc": (a, b) => (b.year || 0) - (a.year || 0),
      "mileage-asc": (a, b) => toNum(a.mileage) - toNum(b.mileage),
      featured: (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0),
    };
    return [...list].sort(sorters[sort] || sorters.featured);
  }, [cars, query, bodyType, fuelType, sort]);

  const activeFilters = (bodyType !== "all") + (fuelType !== "all") + (query.trim() ? 1 : 0);
  const clearFilters = () => { setQuery(""); setBodyType("all"); setFuelType("all"); setSort("featured"); };

  if (!loading && cars.length === 0) return null;

  return (
    <section id="auto-sales" className={`bg-white ${embedded ? "pt-10 pb-16" : "py-24"}`} data-testid="auto-sales-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {!embedded && (
          <Reveal>
            <SectionHeading
              eyebrow="Auto Sales"
              title="Quality vehicles for sale — shipped to Ghana"
              desc="Browse our hand-picked inventory of clean US vehicles. Buy and we'll ship it straight to Tema Port."
            />
          </Reveal>
        )}

        {!loading && (
          <div className="mt-10 rounded-2xl border border-gray-200 bg-navy-50/50 p-3 sm:p-4" data-testid="auto-sales-filters">
            <div className="grid gap-3 md:grid-cols-[1.6fr_1fr_1fr_1.1fr]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/50" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search make, model, feature…"
                  data-testid="car-search"
                  className={`${selectCls} w-full pl-9`}
                />
              </div>
              <select value={bodyType} onChange={(e) => setBodyType(e.target.value)} className={selectCls} data-testid="filter-body">
                <option value="all">All body types</option>
                {bodyTypes.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={fuelType} onChange={(e) => setFuelType(e.target.value)} className={selectCls} data-testid="filter-fuel">
                <option value="all">All fuel types</option>
                {fuelTypes.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} data-testid="sort-cars">
                <option value="featured">Sort: Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="year-desc">Year: Newest</option>
                <option value="mileage-asc">Mileage: Lowest</option>
              </select>
            </div>
            <div className="mt-2.5 flex items-center justify-between px-1">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-navy" data-testid="car-result-count">
                <SlidersHorizontal size={13} /> {visibleCars.length} vehicle{visibleCars.length === 1 ? "" : "s"}
              </p>
              {activeFilters > 0 && (
                <button onClick={clearFilters} data-testid="clear-filters" className="inline-flex items-center gap-1 text-xs font-bold text-brandred hover:underline">
                  <X size={13} /> Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-navy" />
          </div>
        ) : visibleCars.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center" data-testid="no-cars-found">
            <CarIcon className="mx-auto mb-3 text-navy/40" size={32} />
            <p className="font-display text-lg font-bold text-navy">No vehicles match your search</p>
            <p className="mt-1 text-sm text-muted-foreground">Try adjusting your filters or clear them to see all inventory.</p>
            <button onClick={clearFilters} className="mt-4 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">Clear filters</button>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCars.map((c, i) => {
              const sold = c.status === "sold";
              const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                `Hello R.O.K Motors, I'm interested in the ${c.title} (${c.price}). Is it still available?`
              )}`;
              return (
                <Reveal key={c.id} delay={(i % 3) * 0.08}>
                  <div
                    data-testid={`car-card-${i}`}
                    onClick={() => setSelected(c)}
                    className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={c.image_url}
                        alt={c.title}
                        loading="lazy"
                        className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${sold ? "grayscale" : ""}`}
                      />
                      {c.featured && !sold && (
                        <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-xs font-bold text-navy shadow">
                          Featured
                        </span>
                      )}
                      {sold && (
                        <span className="absolute inset-0 flex items-center justify-center bg-navy/60">
                          <span className="rounded-lg bg-brandred px-5 py-2 font-display text-lg font-black text-white">SOLD</span>
                        </span>
                      )}
                      <span className="absolute bottom-3 right-3 rounded-lg bg-navy px-3 py-1.5 font-display text-base font-black text-white shadow-lg">
                        {c.price}
                      </span>
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-lg bg-white/90 px-2.5 py-1 text-xs font-bold text-navy opacity-0 shadow backdrop-blur transition-opacity group-hover:opacity-100">
                        <Eye size={13} /> View details
                      </span>
                    </div>

                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-display text-lg font-bold text-navy">{c.title}</h3>
                      {c.condition && (
                        <span className="mt-1 inline-flex w-fit items-center gap-1 text-xs font-semibold text-gold-700">
                          <BadgeCheck size={14} /> {c.condition}
                        </span>
                      )}
                      {c.description && (
                        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
                      )}

                      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                        {spec(Gauge, c.mileage)}
                        {spec(Cog, c.transmission)}
                        {spec(Fuel, c.fuel_type)}
                        {spec(CarIcon, c.body_type)}
                      </div>

                      <a
                        href={sold ? "#auto-sales" : waLink}
                        target={sold ? undefined : "_blank"}
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`car-enquire-${i}`}
                        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-transform active:scale-95 ${
                          sold
                            ? "cursor-not-allowed bg-gray-100 text-gray-400"
                            : "bg-brandred text-white hover:bg-brandred-700"
                        }`}
                      >
                        <MessageCircle size={16} /> {sold ? "Sold Out" : "Enquire on WhatsApp"}
                      </a>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
      {selected && <CarDetailModal car={selected} onClose={() => setSelected(null)} />}
    </section>
  );
};
